import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { fetchGames, addToWishlist, removeFromWishlist, fetchWishlist } from "../services/api";
import { FaHeart, FaStar, FaFire, FaChartLine, FaEye } from "react-icons/fa";
import { jwtDecode } from "jwt-decode";
import "../styles/Trending.css";

function getUserId() {
  const token = localStorage.getItem("token");
  if (!token) return null;
  try {
    const decoded = jwtDecode(token);
    return decoded.id;
  } catch {
    return null;
  }
}

const Trending = () => {
  const [trendingGames, setTrendingGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [wishlist, setWishlist] = useState([]);
  const [activeTab, setActiveTab] = useState('popularity');

  const token = localStorage.getItem("token");
  const userId = getUserId();

  useEffect(() => {
    loadTrendingGames();
    if (userId && token) {
      fetchWishlist(userId, token)
        .then(items => setWishlist(items.map(item => item.game_id)))
        .catch(() => setWishlist([]));
    }
  }, [userId, token, activeTab]);

  const loadTrendingGames = async () => {
    try {
      setLoading(true);
      let data;
      
      if (activeTab === 'popularity') {
        // Get games sorted by ratings count (popularity)
        data = await fetchGames(1, { ordering: '-ratings_count' });
      } else if (activeTab === 'rating') {
        // Get games sorted by rating
        data = await fetchGames(1, { ordering: '-rating' });
      } else if (activeTab === 'recent') {
        // Get recently released games
        data = await fetchGames(1, { ordering: '-released' });
      }
      
      setTrendingGames(data.results.slice(0, 20)); // Top 20 trending games
    } catch (err) {
      console.error('Error loading trending games:', err);
      setTrendingGames([]);
    } finally {
      setLoading(false);
    }
  };

  const handleWishlistToggle = async (game) => {
    if (!userId || !token) {
      alert("Please log in to add games to your wishlist!");
      return;
    }
    try {
      if (wishlist.includes(game.id)) {
        await removeFromWishlist(userId, game.id, token);
        setWishlist(wishlist => wishlist.filter(id => id !== game.id));
      } else {
        await addToWishlist(userId, game.id, token);
        setWishlist(wishlist => [...wishlist, game.id]);
      }
    } catch (err) {
      console.error('Error toggling wishlist:', err);
    }
  };

  const isInWishlist = (gameId) => wishlist.includes(gameId);

  const getTrendingIcon = (index) => {
    if (index < 3) return <FaFire style={{ color: '#ff6b35' }} />;
    if (index < 10) return <FaChartLine style={{ color: '#4ecdc4' }} />;
    return <FaEye style={{ color: '#95a5a6' }} />;
  };

  if (loading) {
    return (
      <div className="trending-loading">
        <div className="loading-spinner"></div>
        <p>Loading trending games...</p>
      </div>
    );
  }

  return (
    <div className="trending-page">
      <div className="trending-header">
        <h1>🔥 Trending Games</h1>
        <p>Discover the most popular and highly-rated games</p>
      </div>

      <div className="trending-tabs">
        <button 
          className={`tab-btn ${activeTab === 'popularity' ? 'active' : ''}`}
          onClick={() => setActiveTab('popularity')}
        >
          <FaChartLine /> Most Popular
        </button>
        <button 
          className={`tab-btn ${activeTab === 'rating' ? 'active' : ''}`}
          onClick={() => setActiveTab('rating')}
        >
          <FaStar /> Highest Rated
        </button>
        <button 
          className={`tab-btn ${activeTab === 'recent' ? 'active' : ''}`}
          onClick={() => setActiveTab('recent')}
        >
          <FaEye /> Recently Released
        </button>
      </div>

      <div className="trending-grid">
        {trendingGames.map((game, index) => (
          <div key={game.id} className="trending-card">
            <div className="trending-rank">
              {getTrendingIcon(index)}
              <span className="rank-number">#{index + 1}</span>
            </div>
            
            <Link 
              to={`/games/${game.id}`}
              className="trending-card-content"
              style={{ textDecoration: "none", color: "inherit" }}
            >
              <div className="trending-card-img-box">
                <img src={game.background_image} alt={game.name} className="trending-card-img" />
                <div className="trending-overlay">
                  <div className="trending-stats">
                    <div className="stat">
                      <FaStar className="stat-icon" />
                      <span>{Number(game.rating).toFixed(1)}</span>
                    </div>
                    <div className="stat">
                      <FaEye className="stat-icon" />
                      <span>{game.ratings_count ? game.ratings_count.toLocaleString() : "0"}</span>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="trending-card-info">
                <h3 className="trending-game-title">{game.name}</h3>
                <p className="trending-game-released">
                  {game.released ? `Released: ${game.released}` : "Release Date: TBA"}
                </p>
                <div className="trending-game-genres">
                  {game.genres && game.genres.slice(0, 3).map(genre => (
                    <span key={genre.id} className="genre-tag">
                      {genre.name}
                    </span>
                  ))}
                </div>
              </div>
            </Link>

            <button
              className={`trending-wishlist-btn${isInWishlist(game.id) ? " active" : ""}`}
              onClick={() => handleWishlistToggle(game)}
              aria-label={isInWishlist(game.id) ? "Remove from Wishlist" : "Add to Wishlist"}
            >
              <FaHeart />
            </button>
          </div>
        ))}
      </div>

      {trendingGames.length === 0 && (
        <div className="no-trending">
          <p>No trending games available at the moment.</p>
        </div>
      )}
    </div>
  );
};

export default Trending;
